<template>
    <div class="pa-5">
        <v-card v-if="data">
            <v-card-title>
                <h1 class="title">{{ 'Levels' | term }}</h1>
                <v-btn small color="secondary" class="ml-3" v-if="selectedMineArea" @click="create()">
                    <v-icon left>mdi-plus</v-icon>{{ $t('shared.button.add') }}
                </v-btn>
                <v-menu
                    v-model="importDialog"
                    max-width="310px"
                    :close-on-content-click="false"
                    v-if="!!selectedMineArea && !filtered.length"
                >
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" small class="ml-1" color="secondary">
                            <v-icon left>mdi-upload</v-icon>{{ $t('shared.button.import') }}
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-text>
                            <input ref="inputUpload" type="file" accept="text/csv" @change="getFile($event)" />
                        </v-card-text>
                        <v-card-actions>
                            <v-btn color="secondary" @click="importData">{{ $t('shared.button.upload') }}</v-btn>
                            <v-btn text @click="importDialog = false">{{ $t('shared.button.cancel') }}</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>
                <v-btn
                    small
                    class="ml-1"
                    color="gray"
                    v-if="!!selectedMineArea && filtered.length"
                    @click="exportData()"
                >
                    <v-icon left>mdi-download</v-icon>{{ $t('shared.button.export') }}
                </v-btn>
                <v-btn small color="primary" class="ml-3" v-if="selectedMineArea && dirty" @click="saveList()">
                    <v-icon left>mdi-check</v-icon>{{ $t('shared.button.save') }}
                </v-btn>
                <v-btn small color="grey" class="ml-3" v-if="selectedMineArea && dirty" @click="loadData()">
                    <v-icon left>mdi-close</v-icon>{{ $t('shared.button.cancel') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-select
                    :items="mineAreas"
                    item-value="id"
                    item-text="name"
                    v-model="selectedMineArea"
                    :label="$termSync('Mine Area')"
                    single-line
                    hide-details
                    class="admin-table-filter"
                    :menu-props="{ dark: true }"
                ></v-select>
            </v-card-title>
            <standard-wrapping-reorderable
                v-if="!!selectedMineArea"
                :items="filtered"
                :item-id="'id'"
                :item-order="'displayOrder'"
                @reordered="onItemsReordered"
                item-class="reduced-levels-item"
                handle-class="reduced-levels-handle"
            >
                <v-data-table 
                    disable-pagination 
                    hide-default-footer 
                    :headers="headers" 
                    :items="filtered"
                >
                    <template v-slot:item="{ item }">
                        <tr :class="{ dirty: item.dirty, 'reduced-levels-item': true }" :key="`${item.id}-${item.dirty}`">
                            <td>
                                <div class="no-wrap">
                                    <v-icon class="reduced-levels-handle">mdi-drag</v-icon>
                                </div>
                            </td>
                            <td>{{ item.name }}</td>
                            <td>{{ item.displayOrder }}</td>
                            <td>
                                <v-access-control>
                                    <div class="no-wrap">
                                        <v-btn text icon @click="edit(item)">
                                            <v-icon>mdi-pencil</v-icon>
                                        </v-btn>
                                    </div>
                                </v-access-control>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </standard-wrapping-reorderable>
            <empty-state
                v-if="!selectedMineArea"
                icon="mdi-alert-circle-outline"
                :message="$t('shared.message.select-a', { term: $termSync('Mine Area') })"
            ></empty-state>
        </v-card>
        <standard-dialog :render="!!selected" :value='showModal' :max-width="900" persistent>
            <template v-slot:title>
                <span v-if="selected.id">{{ $t('shared.button.edit') }}</span>
                <span v-else>{{ $t('shared.button.create') }}</span>
                <span class="ml-1">{{ 'Level' | term }}</span>
            </template>
            <template v-slot:content>
                <v-form ref="form" :lazy-validation="true">
                    <v-container fluid>
                        <v-row>
                            <v-col cols="12" class="pa-2">
                                <v-text-field
                                    autocomplete="off"
                                    :label="$t('shared.label.name')"
                                    :rules="[$rules.required(), $rules.maxLength(50)]"
                                    v-model="selected.name"
                                    counter="50"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-divider class="my-4" />
                        <v-card>
                            <v-card-title>
                                {{ 'Level' | term }} Map
                            </v-card-title>
                            <v-card-text>
                                <v-img v-if="hasLevelMap" :src="levelMapImageUrl" max-width="400" contain class="mb-3"></v-img>
                                <input ref="inputUpload" type="file" class="d-none" accept="image/*,.pdf" @change="onReplaceLevelMap" />
                            </v-card-text>
                            <v-card-actions>
                                <v-btn text color="red" @click="onRemoveLevelMap" :disabled="mapUploadBusy" v-if="hasLevelMap">{{ $t('shared.button.delete') }}</v-btn>
                                <v-btn text @click="$refs.inputUpload.click()" :disabled="mapUploadBusy">{{ hasLevelMap ? 'Replace' : 'Upload' }}</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-container>
                </v-form>
            </template>
            <template v-slot:actions>
                <v-btn color='red' text v-if='selected && selected.id' @click='del()'>{{ $t('shared.button.delete') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <v-btn color='primary' text @click='save()'>{{ $t('shared.button.save') }}</v-btn>
            </template>
            <template v-slot:offline-actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <offline-btn text></offline-btn>
            </template>
        </standard-dialog>
        <confirm ref="confirm"></confirm>
    </div>
</template>
<script>
import { EventBus, Events } from '@/lib/EventBus';
import MineAreas from '@/lib/data/MineAreas';
import ReducedLevels from '@/lib/data/ReducedLevels';
import DownloadHelper from '@/lib/DownloadHelper';
import StandardWrappingReorderable from '@/components/Shared/Common/StandardWrappingReorderable.vue';
import AppHttp from '@/lib/AppHttp';
import Tenants from '@/lib/Tenants';

export default {
    components: { StandardWrappingReorderable },
    data() {
        return {
            data: null,
            mineAreas: null,
            selected: null,
            selectedMineArea: null,
            showModal: false,
            importDialog: false,
            file: null,
            uploadedMapFile: null,
            mapUploadBusy: false,
            mapCacheKey: Date.now(),
            headers: [
                { text: '', value: 'handle', width: '24px', align: 'left' },
                { text: 'Name', value: 'name', sortable: false, width: '90%' },
                { text: 'Order', value: 'displayOrder', sortable: false, width: '10%' },
                { text: '', value: 'actions', sortable: false, align: 'right' }
            ]
        };
    },
    async created() {
        await this.loadData();
    },
    destroyed() {},
    methods: {
        async loadData() {
            this.mineAreas = await MineAreas.get();
            var d = await ReducedLevels.get();
            for (let x of d) {
                x.dirty = false;
            }
            this.data = d;
        },
        create() {
            var displayOrder = this.filtered.length ? this.filtered[this.filtered.length - 1].displayOrder + 1 : 1;
            this.edit({
                displayOrder: displayOrder,
                mineAreaId: this.selectedMineArea
            });
        },
        edit(item) {
            this.selected = item;
            this.showModal = true;
            this.uploadedMapFile = null;
            this.mapCacheKey = Date.now();
            setTimeout(() => this.$refs.form.resetValidation(), 1);
        },
        async saveList() {
            for (let x of this.data) {
                if (!x.dirty) {
                    continue;
                }
                await ReducedLevels.save(x);
            }
            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Levels')} Saved`);
            await this.loadData();
        },
        async save() {
            if (!this.$refs.form.validate()) {
                return;
            }
            await ReducedLevels.save(this.selected);
            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Level')} Saved`);
            this.showModal = false;
            await this.loadData();
        },
        async del() {
            if (
                await this.$refs.confirm.openAsDeleteResource(this.$termSync('Level'),  { })
            ) {
                await ReducedLevels.delete(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Level')} Deleted`);
                this.showModal = false;
                await this.loadData();
            }
        },
        async exportData() {
            const result = await ReducedLevels.export(this.selectedMineArea);
            var csv = result.csvFile;
            var blob = DownloadHelper.makeBlobFromFileString(csv);
            var selectedMineArea = this.mineAreas.filter(x => x.id == this.selectedMineArea)[0];
            var mineAreaName = selectedMineArea.name.replace(/[/\\?%*:|"<>]/g, '');
            DownloadHelper.download(`ReducedLevels-${mineAreaName}.csv`, blob);
        },
        async importData() {
            try {
                this.$wait.start('saving');
                this.uploadResult = null;
                await ReducedLevels.import(this.selectedMineArea, this.file);
                this.importDialog = false;
                await this.loadData();
            } finally {
                this.$wait.end('saving');
            }
        },
        async getFile(e) {
            this.file = e.target.files[0];
        },
        onItemsReordered(reorderedItems) {
            // Update display orders based on new order
            reorderedItems.forEach((item, index) => {
                const originalItem = this.data.find(x => x.id === item.id);
                if (originalItem && originalItem.displayOrder !== index + 1) {
                    originalItem.displayOrder = index + 1;
                    originalItem.dirty = true;
                }
            });
        },

        async onUploadLevelMap() {
            if (!this.uploadedMapFile) return;
            this.mapUploadBusy = true;
            try {
                const updated = await ReducedLevels.uploadLevelMap(this.selected.id, this.uploadedMapFile);
                this.selected.levelMapImageId = updated.levelMapImageId;
                this.bumpCache();
                this.uploadedMapFile = null;
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Level')} Map Uploaded`);
            } catch (err) {
                EventBus.$emit(Events.ToastError, this.$t('reduced-levels.failed-to-upload-term-map', { term: this.$termSync('Level') }));
            } finally {
                this.mapUploadBusy = false;
            }
        },

        async onReplaceLevelMap(e) {
            const file = e.target.files[0];
            if (!file) return;
            this.uploadedMapFile = file;
            await this.onUploadLevelMap();
            e.target.value = '';
        },

        async onRemoveLevelMap() {
            if (!this.hasLevelMap) return;
            this.mapUploadBusy = true;
            try {
                const updated = await ReducedLevels.deleteLevelMap(this.selected.id);
                this.selected.levelMapImageId = updated.levelMapImageId;
                this.bumpCache();
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Level')} Map Removed`);
            } catch (err) {
                EventBus.$emit(Events.ToastError, this.$t('reduced-levels.failed-to-remove-term-map', { term: this.$termSync('Level') }));
            } finally {
                this.mapUploadBusy = false;
            }
        },

        bumpCache() {
            this.mapCacheKey = Date.now();
        }
    },
    computed: {
        filtered() {
            if (!this.data || !this.selectedMineArea) {
                return [];
            }
            return this.data
                .filter(x => x.mineAreaId == this.selectedMineArea)
                .sort((a, b) => a.displayOrder - b.displayOrder);
        },

        dirty() {
            for (let x of this.data) {
                if (x.dirty) {
                    return true;
                }
            }
            return false;
        },

        hasLevelMap() {
            return !!(this.selected && this.selected.levelMapImageId);
        },

        levelMapImageUrl() {
            if (!this.hasLevelMap) return null;
            return AppHttp.urlFromPath(`Images/GetById/${this.selected.levelMapImageId}?viewTenantId=${Tenants.GetCurrentTenant()}&rnd=${this.mapCacheKey}`);
        }
    }
};
</script>
